/**
 * Reward — post-segment share card screen.
 *
 * Triggers after the user completes all 8 rounds (segment finished from SegmentSummary).
 * Generates a flomo-style minimal share card showing completed 便签 + QR + slogan,
 * which the user can save locally and post to socials.
 *
 * v1 implementation: ShareCard does the actual canvas drawing. This screen is just
 * the container with save / continue / exit actions.
 */
import type { ActiveSegment } from '../state/segment';
import { cardStatusFromUserStatus } from '../state/segment';
import { ShareCard } from '../components/ShareCard';

interface RewardProps {
  segment: ActiveSegment;
  onContinue: () => void;
  onExit: () => void;
}

export function Reward({ segment, onContinue, onExit }: RewardProps) {
  const stats = { done: 0, in_progress: 0, dropped: 0, pending: 0 };
  for (const card of segment.cards) {
    const last = [...segment.traces].reverse().find((t) => t.cardId === card.id);
    const finalStatus = last ? cardStatusFromUserStatus(last.userStatus) : 'pending';
    stats[finalStatus]++;
  }

  const roundCount = segment.traces.length;
  const totalMinutes = roundCount * 5;
  const touchedCount = new Set(segment.traces.map((trace) => trace.cardId)).size;
  const outcomeParts = [
    stats.done > 0 ? `完成 ${stats.done} 张` : null,
    stats.in_progress > 0 ? `${stats.in_progress} 张还在继续` : null,
    stats.dropped > 0 ? `${stats.dropped} 张已经放下` : null,
  ].filter(Boolean);
  const outcomeLine = outcomeParts.length > 0 ? `其中${outcomeParts.join('、')}。` : '';

  return (
    <section className="panel reward">
      <div className="reward-layout">
        <div className="reward-copy">
          <span className="tiny-label">
            <i className="dot lime" /> 今天的启动证明
          </span>
          <h1>今天没有停在开始前</h1>
          <p className="muted">
            你给了自己 {totalMinutes} 分钟,把 {touchedCount} 件事从脑子里推到手边。
            {outcomeLine}保存下来,给明天的自己一个证据。
          </p>
          <div className="reward-proof-strip" aria-label="这一段的成果">
            <span>
              <strong>{roundCount}</strong>
              <em>轮启动</em>
            </span>
            <span>
              <strong>{totalMinutes}</strong>
              <em>分钟坚持</em>
            </span>
            <span>
              <strong>{touchedCount}</strong>
              <em>件事被推进</em>
            </span>
          </div>
        </div>

        <ShareCard segment={segment} />
      </div>

      <div className="actions actions-between reward-actions">
        <button className="btn-ghost" type="button" onClick={onContinue}>
          再开一段
        </button>
        <button className="link-subtle" type="button" onClick={onExit}>
          今天到这
        </button>
      </div>
    </section>
  );
}
